import { Link, useLocation } from '@tanstack/react-router'
import { Menu, TrainFront, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'

import { useOperatingStations } from '@/hooks/useOperatingStations'
import { trackEvent } from '@/lib/analytics'
import {
  type DashboardRequestsCategory,
  useDashboardRequestsStore,
} from '@/lib/stores/dashboardRequests'
import { selectIsStationSelectionLocked, useStationStore } from '@/lib/stores/station'

import { StationAutocomplete } from './StationAutocomplete'

const REQUESTS_FILTERS: { value: DashboardRequestsCategory; labelKey: string }[] = [
  { value: 'all', labelKey: 'dashboard.allBookingRequests' },
  { value: 'arrival', labelKey: 'dashboard.arrivals' },
  { value: 'departure', labelKey: 'dashboard.departures' },
]

const primaryLinkClass =
  'app-nav-link inline-flex min-h-11 items-center rounded-md border border-transparent px-3 py-2 text-sm font-semibold text-white transition-colors hover:border-white/20 hover:bg-white/15 focus-visible:outline-white'
const activePrimaryLinkClass =
  'app-nav-link app-nav-link-active inline-flex min-h-11 items-center rounded-md border border-brand-dark bg-brand-dark px-3 py-2 text-sm font-bold text-white shadow-sm focus-visible:outline-white'
const secondaryLinkClass =
  'app-nav-link inline-flex min-h-10 shrink-0 items-center border-b-2 border-transparent px-0 py-1.5 text-sm font-medium text-text-gray-700 hover:border-brand hover:text-brand-ink'
const activeSecondaryLinkClass =
  'app-nav-link app-nav-link-active inline-flex min-h-10 shrink-0 items-center border-b-2 border-brand-dark px-0 py-1.5 text-sm font-semibold text-brand-ink'

export function GlobalMenuBar() {
  const { t } = useTranslation()
  const auth = useAuth()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isShiftsSection = location.pathname.startsWith('/shifts')
  const isRequestsSection = location.pathname.startsWith('/requests')
  const { stations, selectedStationCode, setSelectedStationCode } = useOperatingStations()
  const isStationSelectionLocked = useStationStore(selectIsStationSelectionLocked)

  const currentCategory = useDashboardRequestsStore((s) => s.filters.category)
  const setFilter = useDashboardRequestsStore((s) => s.setFilter)

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  const userLabel = useMemo(() => {
    const profile = auth.user?.profile
    return profile?.email ?? profile?.preferred_username ?? profile?.name ?? profile?.sub
  }, [auth.user?.profile])

  return (
    <header className="relative z-40 w-full border-b border-brand-dark bg-brand text-white shadow-sm">
      <div className="mx-auto flex min-h-14 w-full flex-wrap items-center gap-x-5 px-4 sm:px-6">
        <div className="flex min-h-14 items-center text-lg font-bold tracking-wide">
          {t('nav.appNameShort')}
        </div>

        <button
          type="button"
          className="ml-auto inline-flex min-h-11 min-w-11 items-center justify-center rounded text-white hover:bg-white/10 focus-visible:outline-white lg:hidden"
          aria-expanded={isMenuOpen}
          aria-controls="global-navigation-content"
          aria-label={isMenuOpen ? t('nav.menuClose') : t('nav.menuOpen')}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          {isMenuOpen ? (
            <X className="size-6" aria-hidden="true" />
          ) : (
            <Menu className="size-6" aria-hidden="true" />
          )}
        </button>

        <div
          id="global-navigation-content"
          className={`${isMenuOpen ? 'flex' : 'hidden'} order-last w-full min-w-0 flex-col gap-3 border-t border-white/30 py-3 lg:order-none lg:flex lg:w-auto lg:flex-1 lg:flex-row lg:items-center lg:border-0 lg:py-0`}
        >
          <nav
            aria-label={t('nav.primaryAriaLabel')}
            className="flex min-w-0 flex-col gap-1 lg:flex-row lg:items-center lg:gap-2"
          >
            <Link
              to="/shift-timeline"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'timeline',
                })
              }
              activeOptions={{ exact: true }}
              className={primaryLinkClass}
              activeProps={{ className: activePrimaryLinkClass, 'aria-current': 'page' }}
            >
              {t('nav.timeline')}
            </Link>
            <Link
              to="/requests"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'requests',
                })
              }
              activeOptions={{ exact: true }}
              className={primaryLinkClass}
              activeProps={{ className: activePrimaryLinkClass, 'aria-current': 'page' }}
            >
              {t('nav.requests')}
            </Link>
            <Link
              to="/shifts"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'shifts',
                })
              }
              className={primaryLinkClass}
              activeProps={{ className: activePrimaryLinkClass, 'aria-current': 'page' }}
            >
              {t('nav.shifts')}
            </Link>
            <Link
              to="/station-manager"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'configuration',
                })
              }
              activeOptions={{ exact: true }}
              className={primaryLinkClass}
              activeProps={{ className: activePrimaryLinkClass, 'aria-current': 'page' }}
            >
              {t('nav.configurations')}
            </Link>
          </nav>

          <div className="order-last flex basis-full items-center justify-center pb-2 lg:order-none lg:ml-auto lg:basis-auto lg:justify-start lg:pb-0">
            <div
              className="flex min-w-0 items-center gap-2"
              title={isStationSelectionLocked ? t('nav.stationChangeLocked') : undefined}
            >
              <TrainFront className="size-5 shrink-0 text-white" aria-hidden="true" />
              <label htmlFor="global-station-selector" className="sr-only">
                {t('nav.stationLabel')}
              </label>
              <StationAutocomplete
                id="global-station-selector"
                value={selectedStationCode}
                stations={stations}
                onChange={(stationCode) => {
                  if (stationCode === selectedStationCode) return
                  setSelectedStationCode(stationCode)
                  trackEvent('station_selected', { area: 'navigation' })
                }}
                disabled={isStationSelectionLocked}
                className="w-[min(20rem,calc(100vw-4rem))] lg:w-72 xl:w-80 [&_input]:h-9 [&_input]:border-white [&_input]:bg-white [&_input]:py-1 [&_input]:shadow-sm"
                inputClassName="font-semibold text-brand-ink"
              />
            </div>
          </div>

          <a
            href="/"
            className="app-nav-link inline-flex min-h-11 shrink-0 items-center rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 hover:text-white focus-visible:outline-white lg:ml-1"
            onClick={(event) => {
              event.preventDefault()
              void auth.signoutRedirect()
            }}
          >
            {userLabel ? t('nav.logOutWithUser', { user: userLabel }) : t('nav.logOut')}
          </a>
        </div>
      </div>

      {isRequestsSection && (
        <div className="w-full overflow-x-auto border-t border-brand/30 bg-white px-4 text-brand-ink shadow-inner sm:px-6">
          <div
            className="flex min-w-max items-center gap-4 py-1"
            role="group"
            aria-label={t('nav.requestFiltersAriaLabel')}
          >
            {REQUESTS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                aria-pressed={currentCategory === filter.value}
                onClick={() => {
                  if (currentCategory === filter.value) return
                  setFilter('category', filter.value)
                  trackEvent('filter_applied', {
                    area: 'requests',
                    filter_type: 'category',
                  })
                }}
                className={`${secondaryLinkClass} ${
                  currentCategory === filter.value
                    ? 'border-brand-dark font-semibold text-brand-ink'
                    : ''
                }`}
              >
                {t(filter.labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}

      {isShiftsSection && (
        <nav
          aria-label={t('resources.tabs.ariaLabel')}
          className="w-full overflow-x-auto border-t border-brand/30 bg-white px-4 text-brand-ink shadow-inner sm:px-6"
        >
          <div className="flex min-w-max items-center gap-4 py-1">
            <Link
              to="/shifts"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'calendar',
                })
              }
              activeOptions={{ exact: true }}
              className={secondaryLinkClass}
              activeProps={{ className: activeSecondaryLinkClass, 'aria-current': 'page' }}
            >
              {t('resources.actions.calendar')}
            </Link>
            <Link
              to="/shifts/default"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'default_schedule',
                })
              }
              search={{ on: undefined }}
              activeOptions={{ exact: true, includeSearch: false }}
              className={secondaryLinkClass}
              activeProps={{ className: activeSecondaryLinkClass, 'aria-current': 'page' }}
            >
              {t('resources.actions.setDefault')}
            </Link>
            <Link
              to="/shifts/override"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'schedule_override',
                })
              }
              search={{ from: undefined, to: undefined }}
              activeOptions={{ exact: true, includeSearch: false }}
              className={secondaryLinkClass}
              activeProps={{ className: activeSecondaryLinkClass, 'aria-current': 'page' }}
            >
              {t('resources.actions.override')}
            </Link>
            <Link
              to="/shifts/activity-log"
              onClick={() =>
                trackEvent('navigation_selected', {
                  area: 'navigation',
                  navigation_target: 'activity_log',
                })
              }
              activeOptions={{ exact: true }}
              className={secondaryLinkClass}
              activeProps={{ className: activeSecondaryLinkClass, 'aria-current': 'page' }}
            >
              {t('resources.actions.activityLog')}
            </Link>
          </div>
        </nav>
      )}
    </header>
  )
}
